/**
 * Create Agent Profile Page - LeadFlow V2.0
 * Full-page profile creation wizard using PrimeReact Diamond template
 */

import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Steps } from 'primereact/steps';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Skeleton } from 'primereact/skeleton';
import { Tooltip } from 'primereact/tooltip';
import ProfileService from '../../services/agentProfile/ProfileService';
import authService from '../../services/auth.service';
import BasicInfoStep from '../../components/agentProfile/wizard/BasicInfoStep';
import WorkExperienceStep from '../../components/agentProfile/wizard/WorkExperienceStep';
import EducationStep from '../../components/agentProfile/wizard/EducationStep';
import CertificationsStep from '../../components/agentProfile/wizard/CertificationsStep';
import SkillsLanguagesStep from '../../components/agentProfile/wizard/SkillsLanguagesStep';
import ReviewStep from '../../components/agentProfile/wizard/ReviewStep';

const CreateProfile = () => {
    const [activeStep, setActiveStep] = useState(0);
    const [initializing, setInitializing] = useState(true);
    const [formData, setFormData] = useState({
        // Basic Info
        full_name: '',
        email: '',
        phone: '',
        headline: '',
        bio: '',
        location_city: '',
        location_state: '',
        location_country: 'India',
        current_company: '',
        current_designation: '',
        experience_years: 0,

        // Account visibility — false = public (default), true = private
        // (private hides this member's posts + comments, profile stays visible)
        is_private_account: false,

        // Product Expertise & Roles (added for new fields)
        product_expertise: [],
        expertise: [],
        specializations: [],
        roles: [],
        employment_types: [], // Salaried, Self-Employed, or both
        service_areas: [],
        associated_companies: [],

        // Work Experience (array) - separated by employment type
        work_experiences: [], // Legacy support
        salaried_experiences: [],
        self_employed_experiences: [],

        // Education (array)
        education: [],

        // Certifications (array)
        certifications: [],

        // Skills (array)
        skills: [],

        // Languages (array)
        languages: []
    });
    const [loading, setLoading] = useState(false);
    const toast = useRef(null);
    const navigate = useNavigate();

    // Fetch current user data on mount to pre-fill basic fields
    useEffect(() => {
        const initializeForm = async () => {
            try {
                setInitializing(true);

                // Get user data from localStorage (set during login)
                const user = authService.getUser();

                if (user) {
                    setFormData(prev => ({
                        ...prev,
                        full_name: user.name || '',
                        email: user.email || '',
                        phone: user.mobile || user.phone || user.mobile_number || ''
                    }));
                }
            } catch (error) {
                // Initialization failed silently
            } finally {
                setInitializing(false);
            }
        };

        initializeForm();
    }, []);

    // Check if mobile
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

    const steps = [
        { label: isMobile ? '1' : 'Basic Info' },
        { label: isMobile ? '2' : 'Work Experience' },
        { label: isMobile ? '3' : 'Education' },
        { label: isMobile ? '4' : 'Certifications' },
        { label: isMobile ? '5' : 'Skills & Languages' },
        { label: isMobile ? '6' : 'Review' }
    ];

    // Full labels for mobile step title
    const stepLabels = ['Basic Info', 'Work Experience', 'Education', 'Certifications', 'Skills & Languages', 'Review'];

    const updateFormData = (updates) => {
        setFormData(prev => ({ ...prev, ...updates }));
    };

    const handleNext = () => {
        if (activeStep < steps.length - 1) {
            setActiveStep(prev => prev + 1);
        }
    };

    const handleBack = () => {
        if (activeStep > 0) {
            setActiveStep(prev => prev - 1);
        }
    };

    const handleSkipToReview = () => {
        setActiveStep(5); // Jump directly to Review step (index 5)
    };

    // Validation function for mandatory fields
    const validateProfile = (profileData) => {
        const errors = {};
        const requiredFields = [
            { field: 'full_name', label: 'Full Name' },
            { field: 'email', label: 'Email' },
            { field: 'phone', label: 'Phone Number' },
            { field: 'headline', label: 'Professional Headline' },
            { field: 'bio', label: 'About/Bio' }
        ];

        // Validate text fields
        requiredFields.forEach(({ field, label }) => {
            const value = profileData[field];
            if (value === null || value === undefined || String(value).trim() === '') {
                errors[field] = `${label} is required`;
            }
        });

        // Validate arrays
        if (!profileData.roles || profileData.roles.length === 0) {
            errors.roles = 'At least one professional role is required';
        }

        if (!profileData.product_expertise || profileData.product_expertise.length === 0) {
            errors.product_expertise = 'At least one product expertise is required';
        }

        if (!profileData.expertise || profileData.expertise.length === 0) {
            errors.expertise = 'At least one area of expertise is required';
        }

        return {
            isValid: Object.keys(errors).length === 0,
            errors
        };
    };

    const handleSubmit = async () => {
        // Validate mandatory fields before submission
        const validation = validateProfile(formData);
        if (!validation.isValid) {
            const errorMessages = Object.values(validation.errors);
            toast.current?.show({
                severity: 'error',
                summary: 'Validation Failed',
                detail: (
                    <div>
                        <p className="mb-2">Please fill in all mandatory fields:</p>
                        <ul className="pl-3 mb-0">
                            {errorMessages.map((msg, idx) => (
                                <li key={idx}>{msg}</li>
                            ))}
                        </ul>
                    </div>
                ),
                life: 6000
            });
            return;
        }

        setLoading(true);

        try {
            const response = await ProfileService.createProfile(formData);

            if (response.success) {
                const lnStatus = response.data?.lending_network_status || response.profile?.lending_network_status;

                if (lnStatus === 'pending') {
                    toast.current?.show({
                        severity: 'info',
                        summary: 'Profile Created — Pending Approval',
                        detail: 'Your Lending Network profile is pending admin approval. You will be notified once approved.',
                        life: 6000
                    });
                } else {
                    toast.current?.show({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Your profile has been created successfully!',
                        life: 3000
                    });
                }

                // Navigate to profile page after short delay
                setTimeout(() => {
                    navigate('/app/my-agent-profile');
                }, 1500);
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: response.message || 'Failed to create profile',
                    life: 4000
                });
            }
        } catch (error) {
            // Enhanced error handling for backend validation
            let errorDetail = 'Failed to create profile';
            if (error.response?.data?.message) {
                errorDetail = error.response.data.message;
            } else if (error.response?.data?.errors) {
                const backendErrors = Object.values(error.response.data.errors).flat();
                errorDetail = (
                    <div>
                        <p className="mb-2">Validation errors:</p>
                        <ul className="pl-3 mb-0">
                            {backendErrors.map((msg, idx) => (
                                <li key={idx}>{msg}</li>
                            ))}
                        </ul>
                    </div>
                );
            }

            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: errorDetail,
                life: 5000
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate('/app/my-agent-profile');
    };

    const renderStepContent = () => {
        switch (activeStep) {
            case 0:
                return <BasicInfoStep data={formData} onUpdate={updateFormData} showAccountVisibility />;
            case 1:
                return <WorkExperienceStep data={formData} onUpdate={updateFormData} />;
            case 2:
                return <EducationStep data={formData} onUpdate={updateFormData} />;
            case 3:
                return <CertificationsStep data={formData} onUpdate={updateFormData} />;
            case 4:
                return <SkillsLanguagesStep data={formData} onUpdate={updateFormData} />;
            case 5:
                return <ReviewStep data={formData} />;
            default:
                return null;
        }
    };

    // Show loading skeleton while initializing
    if (initializing) {
        return (
            <>
                <Toast ref={toast} />
                <div className="grid">
                    <div className="col-12">
                        <Card>
                            <Skeleton width="60%" height="2rem" className="mb-3" />
                            <Skeleton width="40%" height="1rem" className="mb-4" />
                            <Skeleton width="100%" height="4rem" className="mb-3" />
                            <Skeleton width="100%" height="20rem" />
                        </Card>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <Toast ref={toast} />

            <div className="grid">
                {/* Page Header with Colorful Banner */}
                <div className="col-12">
                    <Card className="overflow-hidden p-0">
                        {/* Colorful Gradient Banner */}
                        <div
                            className="relative p-3 md:p-5"
                            style={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                                minHeight: window.innerWidth < 768 ? '100px' : '140px'
                            }}
                        >
                            <div className="flex flex-column align-items-center justify-content-center text-white">
                                <i className="pi pi-user-plus text-4xl md:text-6xl mb-2 md:mb-3 opacity-90"></i>
                                <h2 className="text-white mt-0 mb-1 md:mb-2 font-semibold text-lg md:text-2xl text-center">Create Your Agent Profile</h2>
                                <p className="text-white opacity-90 mt-0 mb-0 text-xs md:text-lg text-center hidden md:block">
                                    Build your professional profile to showcase your expertise
                                </p>
                            </div>
                        </div>
                    </Card>
                </div>

                {/* Progress Steps */}
                <div className="col-12">
                    <Card className="p-2 md:p-3">
                        {/* Mobile: Show current step label */}
                        {isMobile && (
                            <div className="text-center mb-3">
                                <span className="text-primary font-semibold text-sm">
                                    Step {activeStep + 1} of {steps.length}: {stepLabels[activeStep]}
                                </span>
                            </div>
                        )}
                        <Steps
                            model={steps}
                            activeIndex={activeStep}
                            onSelect={(e) => setActiveStep(e.index)}
                            readOnly={false}
                            className="mb-2 md:mb-4"
                        />
                        {/* Hide step labels on mobile - only show circles */}
                        {isMobile && (
                            <style>{`
                                .p-steps .p-steps-item .p-steps-title {
                                    display: none;
                                }
                            `}</style>
                        )}
                    </Card>
                </div>

                {/* Form Content */}
                <div className="col-12">
                    <Card>
                        {renderStepContent()}

                        {/* Navigation Buttons - Skip button visible on step 0 */}
                        <div className={`flex mt-4 md:mt-5 pt-3 md:pt-4 border-top-1 surface-border ${isMobile ? 'flex-column gap-2' : 'justify-content-between'}`}>
                            {/* Left buttons - Cancel and Previous */}
                            <div className={`flex gap-2 ${isMobile ? 'order-2 justify-content-center' : ''}`}>
                                <Button
                                    label={isMobile ? "" : "Cancel"}
                                    icon="pi pi-times"
                                    className="p-button-text p-button-secondary p-button-sm md:p-button-md"
                                    onClick={handleCancel}
                                    disabled={loading}
                                    tooltip={isMobile ? "Cancel" : undefined}
                                    tooltipOptions={{ position: 'top' }}
                                />
                                {activeStep > 0 && (
                                    <Button
                                        label={isMobile ? "" : "Previous"}
                                        icon="pi pi-chevron-left"
                                        className="p-button-outlined p-button-sm md:p-button-md"
                                        onClick={handleBack}
                                        disabled={loading}
                                        tooltip={isMobile ? "Previous" : undefined}
                                        tooltipOptions={{ position: 'top' }}
                                    />
                                )}
                            </div>

                            {/* Right buttons - Skip to Review and Next/Submit */}
                            <div className={`flex gap-2 align-items-center ${isMobile ? 'order-1 justify-content-center' : ''}`}>
                                {activeStep === 0 && (
                                    <Button
                                        label={isMobile ? "Skip" : "Skip to Review"}
                                        icon="pi pi-forward"
                                        iconPos="right"
                                        className="p-button-warning p-button-sm md:p-button-md"
                                        onClick={handleSkipToReview}
                                        disabled={loading}
                                    />
                                )}
                                {activeStep < steps.length - 1 ? (
                                    <Button
                                        label="Next"
                                        icon="pi pi-chevron-right"
                                        iconPos="right"
                                        className="p-button-sm md:p-button-md"
                                        onClick={handleNext}
                                        disabled={loading}
                                    />
                                ) : (
                                    <Button
                                        label={isMobile ? "Create" : "Create Profile"}
                                        icon="pi pi-check"
                                        className="p-button-sm md:p-button-md"
                                        loading={loading}
                                        onClick={handleSubmit}
                                    />
                                )}
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
        </>
    );
};

export default CreateProfile;
